import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";

/**
 * Native touch-based pull-to-refresh. Trigger threshold = 80px.
 * Only activates when the scroll container is at scrollTop === 0 so it
 * never fights with normal scrolling. Renders an animated spinner above
 * the children while the user is pulling.
 */
export default function PullToRefresh({ onRefresh, children, testId = "pull-to-refresh" }) {
  const [distance, setDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const wrapRef = useRef(null);
  const THRESHOLD = 80;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const onStart = (e) => {
      if (refreshing) return;
      // Only start when the whole document is scrolled to top
      if (window.scrollY > 4) return;
      startY.current = e.touches[0].clientY;
    };
    const onMove = (e) => {
      if (startY.current == null || refreshing) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy > 0) {
        // Dampen: pull feels heavier as it stretches
        const eased = Math.min(120, Math.round(Math.pow(dy, 0.85)));
        setDistance(eased);
        if (dy > 12) e.preventDefault();
      }
    };
    const onEnd = async () => {
      if (startY.current == null || refreshing) { startY.current = null; return; }
      startY.current = null;
      if (distance >= THRESHOLD && onRefresh) {
        setRefreshing(true);
        try { await onRefresh(); }
        catch { /* swallow — caller handles */ }
        setRefreshing(false);
      }
      setDistance(0);
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
    };
  }, [distance, refreshing, onRefresh]);

  const triggered = distance >= THRESHOLD || refreshing;
  const indicatorHeight = refreshing ? 44 : Math.min(60, distance);

  return (
    <div ref={wrapRef} data-testid={testId} className="min-h-full">
      <div
        style={{ height: indicatorHeight }}
        className="flex items-end justify-center overflow-hidden transition-[height] duration-150"
        aria-hidden={indicatorHeight === 0}>
        {indicatorHeight > 4 && (
          <div className={`pb-2 flex items-center gap-2 text-xs ${triggered ? "text-amber-400" : "text-slate-500"}`}
               data-testid="ptr-indicator">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} style={!refreshing ? { transform: `rotate(${distance * 3}deg)` } : {}}/>
            <span>{refreshing ? "Refreshing…" : triggered ? "Release to refresh" : "Pull to refresh"}</span>
          </div>
        )}
      </div>
      {children}
    </div>
  );
}
